import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, MapPin, Clock, AlertCircle } from 'lucide-react';
import apiService from './apiService';

// Translations object outside component to avoid recreation on each render
const TRANSLATIONS = {
  fr: {
    currentPassengers: 'Passagers actuels',
    loading: 'Chargement...',
    refresh: 'Rafraîchir',
    autoRefresh: 'Actualisation automatique',
    errorFetch: 'Impossible de récupérer la liste des passagers',
    noPassengers: 'Aucun passager actuellement à bord',
    departure: 'Départ:',
    arrival: 'Arrivée:',
    distance: 'Distance:',
    notes: 'Notes:',
    done: 'Terminé',
    processing: 'Traitement...',
    refreshing: '✓ Actualisation en cours',
    stopped: '⊘ Actualisation arrêtée'
  },
  en: {
    currentPassengers: 'Current Passengers',
    loading: 'Loading...',
    refresh: 'Refresh',
    autoRefresh: 'Auto-refresh',
    errorFetch: 'Unable to fetch passenger list',
    noPassengers: 'No passengers on board',
    departure: 'Departure:',
    arrival: 'Arrival:',
    distance: 'Distance:',
    notes: 'Notes:',
    done: 'Done',
    processing: 'Processing...',
    refreshing: '✓ Refreshing',
    stopped: '⊘ Stopped'
  },
  sw: {
    currentPassengers: 'Abiria Wanaosafiri',
    loading: 'Kupakia...',
    refresh: 'Onyesha upya',
    autoRefresh: 'Onyesha upya otomatiki',
    errorFetch: 'Haiwezi kupata orodha ya abiria',
    noPassengers: 'Hakuna abiria ndani',
    departure: 'Kuondoka:',
    arrival: 'Kuwasili:',
    distance: 'Umbali:',
    notes: 'Vidokezo:',
    done: 'Kumalizika',
    processing: 'Inachakata...',
    refreshing: '✓ Inaonyesha upya',
    stopped: '⊘ Imesimama'
  },
  ln: {
    currentPassengers: 'Bato Oyo Basafula',
    loading: 'Kocheka...',
    refresh: 'Zongisa',
    autoRefresh: 'Zongisa otomatiki',
    errorFetch: 'Haiwezi kupata orodha ya abiria',
    noPassengers: 'Azali na bato te na kati',
    departure: 'Kozali:',
    arrival: 'Kolanda:',
    distance: 'Mosika:',
    notes: 'Bitengeneze:',
    done: 'Esilaki',
    processing: 'Osali...',
    refreshing: '✓ Osali zongisa',
    stopped: '⊘ Esimamaki'
  }
};

const CurrentPassengers = ({ driverId, language = 'fr' }) => {
  const [passengers, setPassengers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Use memoized translations to prevent recreation
  const t = useMemo(() => TRANSLATIONS[language] || TRANSLATIONS.fr, [language]);

  // Fetch current passengers - stable function reference
  const fetchCurrentPassengers = useCallback(async () => {
    if (!driverId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.get(`/trips/active/${driverId}`, { userType: 'driver' });
      
      if (data.success) {
        setPassengers(data.currentPassengers);
        setTotalCount(data.count);
      } else {
        setError(data.error || t.errorFetch);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(t.errorFetch);
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  // Mark passenger as exited
  const handlePassengerExit = async (tripId, passengerName) => {
    try {
      setCompletingId(tripId);
      
      const data = await apiService.post(`/trips/${tripId}/passenger-exit`, {}, { userType: 'driver' });
      
      if (data.success) {
        // Remove passenger from list
        setPassengers(passengers.filter(p => p.trip_id !== tripId));
        setTotalCount(prev => prev - 1);
      } else {
        setError(data.error || 'Erreur lors de la validation');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de marquer le passager comme descendu');
    } finally {
      setCompletingId(null);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchCurrentPassengers();
  }, [fetchCurrentPassengers]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchCurrentPassengers();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchCurrentPassengers, autoRefresh]);

  return (
    <div className="current-passengers-container" style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <Users size={24} style={{ color: '#F54119' }} />
          <h2 style={styles.title}>{t.currentPassengers}</h2>
          <span style={styles.badge}>{totalCount}</span>
        </div>
        <button
          onClick={fetchCurrentPassengers}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? `⟳ ${t.loading}` : `⟳ ${t.refresh}`}
        </button>
      </div>

      {/* Auto-refresh toggle */}
      <div style={styles.toggleSection}>
        <label style={styles.toggle}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          {t.autoRefresh}
        </label>
      </div>

      {/* Error message */}
      {error && (
        <div style={styles.errorBox}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && totalCount === 0 && !error && (
        <div style={styles.emptyState}>
          <Users size={48} style={{ color: '#DCE0E6', marginBottom: '12px' }} />
          <p style={styles.emptyText}>{t.noPassengers}</p>
        </div>
      )}

      {/* Passengers list */}
      {totalCount > 0 && (
        <div style={styles.passengersList}>
          {passengers.map((passenger) => (
            <div key={passenger.trip_id} style={styles.passengerCard}>
              {/* Passenger info */}
              <div style={styles.passengerInfo}>
                <div style={styles.passengerHeader}>
                  {passenger.passenger_photo ? (
                    <img
                      src={passenger.passenger_photo}
                      alt={passenger.passenger_name}
                      style={styles.avatar}
                    />
                  ) : (
                    <div style={styles.avatarPlaceholder}>
                      {passenger.passenger_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={styles.nameSection}>
                    <h3 style={styles.passengerName}>{passenger.passenger_name}</h3>
                    {passenger.route_name && (
                      <p style={styles.route}>{passenger.route_name}</p>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div style={styles.details}>
                  {passenger.start_location && (
                    <div style={styles.detail}>
                      <MapPin size={16} style={{ color: '#059669' }} />
                      <span style={styles.detailText}>
                        {t.departure} {passenger.start_location.name || `${passenger.start_location.latitude.toFixed(4)}, ${passenger.start_location.longitude.toFixed(4)}`}
                      </span>
                    </div>
                  )}
                  
                  {passenger.end_location && (
                    <div style={styles.detail}>
                      <MapPin size={16} style={{ color: '#dc2626' }} />
                      <span style={styles.detailText}>
                        {t.arrival} {passenger.end_location.name || `${passenger.end_location.latitude.toFixed(4)}, ${passenger.end_location.longitude.toFixed(4)}`}
                      </span>
                    </div>
                  )}

                  {passenger.distance_km && (
                    <div style={styles.detail}>
                      <Clock size={16} style={{ color: '#FF7800' }} />
                      <span style={styles.detailText}>{t.distance} {passenger.distance_km} km</span>
                    </div>
                  )}

                  {passenger.notes && (
                    <div style={styles.notesBox}>
                      <strong>{t.notes}</strong> {passenger.notes}
                    </div>
                  )}
                </div>
              </div>

              {/* Action button */}
              <button
                onClick={() => handlePassengerExit(passenger.trip_id, passenger.passenger_name)}
                disabled={completingId === passenger.trip_id}
                style={{
                  ...styles.exitButton,
                  opacity: completingId === passenger.trip_id ? 0.7 : 1,
                  cursor: completingId === passenger.trip_id ? 'not-allowed' : 'pointer'
                }}
              >
                {completingId === passenger.trip_id ? (
                  <>⊙ {t.processing}</>
                ) : (
                  <>✓ {t.done}</>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Status indicator */}
      {totalCount > 0 && (
        <div style={styles.statusBar}>
          <span style={styles.statusText}>
            {autoRefresh ? `✓ ${t.refreshing}` : `⊘ ${t.stopped}`}
          </span>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '16px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E9EBEF',
    marginTop: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0',
    color: '#1C1C1C'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    backgroundColor: '#FF7800',
    color: 'white',
    borderRadius: '50%',
    fontSize: '14px',
    fontWeight: '700'
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#FF7800',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  toggleSection: {
    padding: '12px',
    backgroundColor: '#FEE9E2',
    borderRadius: '8px',
    marginBottom: '16px'
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#1C1C1C',
    cursor: 'pointer',
    fontWeight: '500'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#991b1b',
    marginBottom: '16px',
    fontSize: '14px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px dashed #DCE0E6'
  },
  emptyText: {
    color: '#5C636E',
    fontSize: '14px',
    margin: '0'
  },
  passengersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  passengerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #E9EBEF',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'box-shadow 0.2s'
  },
  passengerInfo: {
    flex: 1,
    minWidth: 0
  },
  passengerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #E9EBEF'
  },
  avatarPlaceholder: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#FF7800',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '20px',
    flexShrink: 0
  },
  nameSection: {
    flex: 1,
    minWidth: 0
  },
  passengerName: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#1C1C1C'
  },
  route: {
    fontSize: '14px',
    color: '#5C636E',
    margin: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  detail: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#414852'
  },
  detailText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  notesBox: {
    fontSize: '13px',
    padding: '8px',
    backgroundColor: '#fef3c7',
    borderRadius: '4px',
    color: '#92400e',
    borderLeft: '3px solid #FFB900'
  },
  exitButton: {
    marginLeft: '12px',
    padding: '10px 16px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'background-color 0.2s'
  },
  statusBar: {
    marginTop: '16px',
    padding: '10px 12px',
    backgroundColor: '#FEE9E2',
    borderRadius: '6px',
    textAlign: 'center'
  },
  statusText: {
    fontSize: '13px',
    color: '#C9350F',
    fontWeight: '500'
  }
};

export default CurrentPassengers;
